import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Printer, FileText } from "lucide-react";
import { useApi, errMsg, inr, lastMonth } from "../lib/api";
import { PageHeader, VendorMonthBar, useVendors } from "../components/shared";
import { Button } from "../components/ui/button";

const td = "border border-gray-400 px-1.5 py-1";

const Sheet = ({ d }) => (
  <div className="bg-white text-black rounded-lg p-6 overflow-x-auto text-[11px]" data-testid="printable-wage-register-container">
    <div className="text-center mb-4">
      <div className="font-bold uppercase tracking-wider text-xs">Form IV / XV / 13</div>
      <div className="font-bold uppercase text-base">Register of Wages</div>
      <div className="text-gray-600">[Rules under the Contract Labour (Regulation &amp; Abolition) Central Framework Rules]</div>
    </div>
    <div className="grid grid-cols-2 gap-4 mb-4 border-b border-gray-300 pb-3">
      <div>
        <p><b>Principal Employer:</b> {d.employer?.company_name}</p>
        <p><b>CIN:</b> {d.employer?.cin_number}</p>
        <p><b>Address:</b> {d.employer?.registered_office_address}</p>
      </div>
      <div className="text-right">
        <p><b>Contractor / Vendor:</b> {d.vendor.vendor_name}</p>
        <p><b>EPF Code:</b> {d.vendor.epf_registration_number} · <b>ESIC:</b> {d.vendor.esic_registration_number}</p>
        <p><b>CLRA Licence:</b> {d.vendor.clra_license_number || "—"} · <b>Wage Period:</b> {d.month}</p>
      </div>
    </div>
    <table className="w-full border-collapse">
      <thead><tr className="bg-gray-100 uppercase text-[10px] font-bold">
        {["Sr.", "Worker Name / UAN", "Category", "Days", "Basic Earned", "Gross Pay", "PF Ded. (12%)", "ESIC (0.75%)", "Net Payout", "Signature"].map((h) => <th key={h} className={`${td} text-left`}>{h}</th>)}
      </tr></thead>
      <tbody className="font-mono">
        {d.records.map((r, i) => (
          <tr key={r.id} className={r.min_wage_breach ? "bg-red-50 text-red-900" : ""}>
            <td className={`${td} text-center`}>{i + 1}</td>
            <td className={`${td} font-sans font-semibold`}>{r.worker_full_name}<br /><span className="text-[9px] text-gray-500 font-mono">UAN: {r.uan_number || "N/A"}</span></td>
            <td className={`${td} font-sans`}>{r.skill_category}</td>
            <td className={`${td} text-center`}>{r.days_worked}</td>
            <td className={`${td} text-right`}>{inr(r.basic_wages_for_pf)}</td>
            <td className={`${td} text-right`}>{inr(r.gross_wages_earned)}</td>
            <td className={`${td} text-right text-red-700`}>-{inr(r.calculated_pf_employee)}</td>
            <td className={`${td} text-right text-red-700`}>-{inr(r.calculated_esic_employee)}</td>
            <td className={`${td} text-right font-bold text-green-800`}>{inr(r.net_payout)}</td>
            <td className={`${td} w-20`} />
          </tr>
        ))}
        <tr className="bg-gray-50 font-bold uppercase text-[10px]">
          <td colSpan={3} className={`${td} text-right`}>Totals ({d.totals.headcount} workers)</td>
          <td className={`${td} text-center`}>{d.totals.days}</td>
          <td className={`${td} text-right`}>{inr(d.totals.basic)}</td>
          <td className={`${td} text-right`}>{inr(d.totals.gross)}</td>
          <td className={`${td} text-right text-red-700`}>-{inr(d.totals.pf)}</td>
          <td className={`${td} text-right text-red-700`}>-{inr(d.totals.esic)}</td>
          <td className={`${td} text-right text-green-800`}>{inr(d.totals.net)}</td>
          <td className={td} />
        </tr>
      </tbody>
    </table>
    <div className="flex justify-between mt-10 text-[10px] text-gray-600">
      <span>Rows highlighted red are below the ₹450/day minimum wage floor.</span>
      <span>Signature of Contractor / Authorised Representative ____________________</span>
    </div>
  </div>
);

export default function Register() {
  const api = useApi();
  const { data: vendors } = useVendors();
  const [vendorId, setVendorId] = useState("");
  const [month, setMonth] = useState(lastMonth());
  const [d, setD] = useState(null);
  useEffect(() => { if (!vendorId && vendors?.length) setVendorId(vendors[0].id); }, [vendors, vendorId]);

  const gen = async () => {
    try { setD((await api.get(`/registers/wage-register?vendor_id=${vendorId}&month=${month}`)).data); }
    catch (e) { setD(null); toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="no-print">
        <PageHeader eyebrow="Statutory documents" title="Register of wages" desc="Form IV / XV / 13, printed on A4 landscape. Use your browser's print dialog to save it as a PDF.">
          {d && <Button onClick={() => window.print()} data-testid="print-register-btn" className="bg-[#00E599] text-[#0B1017] hover:bg-[#00C482] font-semibold"><Printer size={15} className="mr-2" />Print / Save PDF</Button>}
        </PageHeader>
        <VendorMonthBar vendors={vendors} vendorId={vendorId} setVendorId={setVendorId} month={month} setMonth={setMonth}>
          <Button onClick={gen} disabled={!vendorId} data-testid="generate-register-btn" variant="outline" className="h-10 border-[#1F2A3E] hover:bg-[#1A2234] hover:text-white"><FileText size={14} className="mr-2" />Generate</Button>
        </VendorMonthBar>
      </div>
      {d && <Sheet d={d} />}
    </div>
  );
}
